
import React, { useEffect, useRef } from 'react';
import { useIsMobile } from '../hooks/use-mobile';
import { useChatInterface } from '../hooks/useChatInterface';
import { useAnalyticsTracking } from '../hooks/useAnalyticsTracking';
import { useChatAutoExpand } from '../hooks/useChatAutoExpand';
import { useStateManager } from '../hooks/useStateManager';
import { useSessionRecovery } from '../hooks/useSessionRecovery';
import { useDealerSession } from '../contexts/DealerSessionContext';
import { toast } from 'sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import CollapsedChatButton from './chat/CollapsedChatButton';
import ChatLayoutMobile from './chat/ChatLayoutMobile';
import ChatLayoutDesktop from './chat/ChatLayoutDesktop';
import SMSScreenshotModal from './SMSScreenshotModal';
import { widgetDiagBeacon } from '@/utils/widgetDiagnostics';

const ChatInterface = () => {
  const isMobile = useIsMobile();
  const { trackChatStarted } = useAnalyticsTracking();
  const { isRestoring: stateRestoring } = useStateManager();
  const { sessionData } = useDealerSession();
  
  // Local state for collapsed icon dismissal (no persistence)
  const [isCollapsedIconDismissed, setIsCollapsedIconDismissed] = React.useState(false);
  // Track if user manually closed the chat or dismissed the icon (to disable tooltips) - persisted in sessionStorage
  const [userManuallyClosed, setUserManuallyClosed] = React.useState(() => {
    try {
      return sessionStorage.getItem('d2c-chat-tooltips-disabled') === 'true';
    } catch {
      return false;
    }
  });

  // Enhanced session recovery with visual feedback
  const { isSessionStale, isRecovering } = useSessionRecovery({
    maxInactivityHours: 3, // Trigger recovery after 3 hours of inactivity
    onRecoveryComplete: () => {
      // Recovery completed
    }
  });
  
  const {
    // State
    input,
    setInput,
    isExpanded,
    setIsExpanded,
    showNewConversationDialog,
    setShowNewConversationDialog,
    showSMSScreenshotModal,
    setShowSMSScreenshotModal,
    screenshotUrl,
    
    // Chat state
    messages,
    isRestoring,
    isLoading,
    isSearching,
    chatId,
    sessionId,
    dealerId,
    streamingIntro,
    
    // Form state
    activeForm,
    isOfferHighlighted,
    formData,
    updateFormData,
    closeForm,
    
    // Handlers
    handleFormSubmit,
    handleNewConversationClick,
    handleConfirmNewConversation,
    handleCopyConversation,
    handleSendMessage,
    
    // Shift Digital tracking
    shiftTracking,
    
    // Retry mechanism
    retryLastRequest,
    
    // Waiting video
    waitingVideoData,
  } = useChatInterface();

  // Check if vector store is missing
  const hasVectorStoreError = sessionData && (!sessionData.vectorStoreId || sessionData.vectorStoreId === '');

  // Auto-expand functionality - disabled when vector store is missing.
  // `onExpand` is memoized because useChatAutoExpand lists it in a useEffect
  // dependency array whose cleanup clears the pending auto-expand timers. As an
  // inline arrow it changed identity on every render, so any re-render re-armed the
  // 1.5s timer from zero and auto-expand could never actually fire.
  const handleAutoExpand = React.useCallback(() => {
    trackChatStarted();
    // Dispatch event for host site to detect chat expansion
    if (isMobile) {
      window.dispatchEvent(new CustomEvent('d2c-chat-expanded', { detail: { expanded: true } }));
    }
  }, [trackChatStarted, isMobile]);

  useChatAutoExpand({
    isExpanded,
    setIsExpanded,
    disabled: hasVectorStoreError,
    onExpand: handleAutoExpand
  });

  // Handle dismissal of collapsed icon (temporary, no persistence)
  const handleDismissCollapsed = () => {
    setIsCollapsedIconDismissed(true);
    setIsExpanded(false);
    setUserManuallyClosed(true);
    try {
      sessionStorage.setItem('d2c-chat-tooltips-disabled', 'true');
    } catch { /* ignore */ }
  };
  // Diagnostic beacons: prove the widget React tree mounted, and that we
  // reached a real render path (not stuck in the loading spinner).
  useEffect(() => {
    widgetDiagBeacon('widget-mounted');
  }, []);
  // Only the first transition into a rendered state is diagnostically useful.
  // Previously this effect listed isExpanded/isCollapsedIconDismissed as
  // dependencies, so it re-fired on every expand/collapse toggle.
  const visibleBeaconSent = useRef(false);
  useEffect(() => {
    if (visibleBeaconSent.current) return;
    if (!stateRestoring && !isRecovering) {
      visibleBeaconSent.current = true;
      widgetDiagBeacon('widget-visible');
    }
  }, [stateRestoring, isRecovering]);

  // Show loading while state is restoring or recovering from long inactivity
  if (stateRestoring || isRecovering) {
    const message = isRecovering ? 'Waking up after inactivity...' : 'Loading...';
    return <div className="fixed inset-0 flex items-center justify-center bg-background/80">
      <div className="text-muted-foreground flex items-center gap-2">
        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
        {message}
      </div>
    </div>;
  }

  // Collapsed state - floating chat button with animations
  // Don't show if collapsed icon was dismissed
  if (!isExpanded && !isCollapsedIconDismissed) {
    return <CollapsedChatButton 
      errorMessage={hasVectorStoreError ? 'Missing Chatbot Vector Store' : undefined}
      disableTooltips={userManuallyClosed}
      onExpand={() => {
        // If there's an error, don't expand
        if (hasVectorStoreError) {
          return;
        }
        
        // Track manual click for Shift Digital
        shiftTracking.trackClick();
        
        trackChatStarted();
        setIsExpanded(true);
        // Dispatch event for host site to detect chat expansion
        if (isMobile) {
          window.dispatchEvent(new CustomEvent('d2c-chat-expanded', { detail: { expanded: true } }));
        }
      }}
      onDismiss={handleDismissCollapsed}
    />;
  }

  // If collapsed icon dismissed and not expanded, don't render anything
  if (isCollapsedIconDismissed && !isExpanded) {
    return null;
  }

  // Common props for both layouts
  const layoutProps = {
    messages,
    isLoading,
    isSearching,
    isRestoring,
    chatId,
    sessionId,
    dealerId,
    waitingVideoData,
    streamingIntro,
    activeForm,
    isOfferHighlighted,
    formData,
    updateFormData,
    closeForm,
    input,
    setInput,
    showNewConversationDialog,
    setShowNewConversationDialog,
    onNewConversation: handleNewConversationClick,
    onCopyConversation: handleCopyConversation,
    onCollapse: () => {
      // Track manual close for Shift Digital
      shiftTracking.trackClosed();
      
      setIsExpanded(false);
      setUserManuallyClosed(true);
      try {
        sessionStorage.setItem('d2c-chat-tooltips-disabled', 'true');
      } catch { /* ignore */ }
      // Dispatch event for host site to detect chat collapse
      if (isMobile) {
        window.dispatchEvent(new CustomEvent('d2c-chat-expanded', { detail: { expanded: false } }));
      }
    },
    onFormSubmit: handleFormSubmit,
    onSendMessage: handleSendMessage,
    onConfirmNewConversation: handleConfirmNewConversation,
    onRetryLastRequest: retryLastRequest,
  };

  // Mobile full-screen layout
  if (isMobile) {
    return (
      <TooltipProvider>
        <ChatLayoutMobile {...layoutProps} />
        <SMSScreenshotModal 
          open={showSMSScreenshotModal} 
          onOpenChange={setShowSMSScreenshotModal}
          screenshotUrl={screenshotUrl}
        />
      </TooltipProvider>
    );
  }

  // Desktop layout - expanded state - full chat interface with form framework
  return (
    <TooltipProvider>
      <ChatLayoutDesktop {...layoutProps} />
      <SMSScreenshotModal 
        open={showSMSScreenshotModal} 
        onOpenChange={setShowSMSScreenshotModal}
        screenshotUrl={screenshotUrl}
      />
    </TooltipProvider>
  );
};

export default ChatInterface;
